import React from 'react';

const PRINT_LOGO_SRC = '/logo512.png';
const PRINT_WATERMARK_SRC = '/logo192.png';
const RAPID_MODEL_IMAGE_SRC = '/rapid.jpeg';
const FULL_PRINT_SURFACE_WIDTH_MM = 186;
const FULL_PRINT_PAGE_MIN_HEIGHT_MM = 273;

const pageBaseStyle = {
    minHeight: `${FULL_PRINT_PAGE_MIN_HEIGHT_MM}mm`,
    boxSizing: 'border-box',
    padding: '6mm'
};

const surfaceStyle = {
    width: `${FULL_PRINT_SURFACE_WIDTH_MM}mm`
};

const partBChips = (report) => [
    { label: 'Resources', value: report.partB.resolvedChoices.resources.label },
    { label: 'Attitude', value: report.partB.resolvedChoices.attitude.label },
    { label: 'Perspectives', value: report.partB.resolvedChoices.perspectives.label },
    { label: 'Decision Making', value: report.partB.resolvedChoices.decision.label }
];

const PageFrame = ({ pageLabel, title, subtitle, children }) => (
    <section
        className="full-results-dedicated-print-page relative overflow-hidden rounded-[20px] border border-[#ece4dc] bg-white text-[#171717] shadow-[0_14px_32px_rgba(15,23,42,0.05)]"
        style={pageBaseStyle}
    >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(243,119,65,0.10),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(146,39,143,0.08),_transparent_36%),linear-gradient(180deg,_rgba(255,255,255,0.98)_0%,_rgba(255,252,249,1)_100%)]" />
        <div
            className="pointer-events-none absolute inset-[6mm] opacity-[0.04]"
            style={{
                backgroundImage: `url(${PRINT_WATERMARK_SRC})`,
                backgroundRepeat: 'repeat',
                backgroundSize: '18mm 18mm',
                backgroundPosition: '0 0'
            }}
        />

        <div className="relative z-10 flex min-h-full flex-col gap-3">
            <header className="flex items-start justify-between gap-3 border-b border-[#eee7df] pb-2.5">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-[14px] border border-[#f1e8de] bg-white p-1.5 shadow-sm">
                        <img src={PRINT_LOGO_SRC} alt="Yancha" className="h-full w-full object-contain" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#92278F]">Yancha Crystal Report</p>
                        <h1 className="mt-1 text-[18px] leading-tight font-black text-[#171717] brand-font">{title}</h1>
                    </div>
                </div>

                <div className="max-w-[165px] text-right">
                    <p className="text-[8px] font-black uppercase tracking-[0.22em] text-[#8a7d74]">{pageLabel}</p>
                    <p className="mt-1 text-[10px] leading-[1.35] font-semibold text-[#4b5563] brand-text">{subtitle}</p>
                </div>
            </header>

            <div className="relative z-10 min-h-0 flex-1">{children}</div>
        </div>
    </section>
);

const SectionTitle = ({ eyebrow, title, intro }) => (
    <div className="mb-3">
        {eyebrow ? <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#92278F]">{eyebrow}</p> : null}
        <h2 className="mt-1 text-[20px] leading-tight font-black text-[#171717] brand-font">{title}</h2>
        {intro ? <p className="mt-1.5 text-[10px] leading-[1.45] text-[#57534e] brand-text">{intro}</p> : null}
    </div>
);

const TextCard = ({ title, paragraphs, note, className = '' }) => (
    <section className={`full-results-dedicated-print-card rounded-[18px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm ${className}`}>
        {title ? <h3 className="text-[15px] leading-tight font-black text-[#171717] brand-font">{title}</h3> : null}
        <div className={`${title ? 'mt-2' : ''} space-y-1.5`}>
            {paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-[9.4px] leading-[1.42] text-[#57534e] brand-text">{paragraph}</p>
            ))}
        </div>
        {note ? <p className="mt-2.5 text-[9.4px] leading-[1.42] text-[#57534e] brand-text">{note}</p> : null}
    </section>
);

const GuidanceCard = () => (
    <div className="full-results-dedicated-print-card rounded-[14px] border border-[#eadff0] bg-[linear-gradient(135deg,_rgba(146,39,143,0.08),_rgba(243,119,65,0.08))] px-3 py-2.5 text-center shadow-sm">
        <p className="text-[10px] leading-tight font-black text-[#171717] brand-font">Contact your Yancha Career Councellor for InDepth Analysis</p>
    </div>
);

const RecommendationPrintCard = ({ paragraphs }) => (
    <section className="full-results-dedicated-print-card rounded-[18px] border border-[#eadff0] bg-[linear-gradient(135deg,_rgba(146,39,143,0.08),_rgba(243,119,65,0.08))] px-3.5 py-3 shadow-sm">
        <div className="space-y-1.5">
            {paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-[9.4px] leading-[1.42] text-[#57534e] brand-text">{paragraph}</p>
            ))}
        </div>
    </section>
);

const Chip = ({ label, value }) => (
    <span className="inline-flex items-center gap-1 rounded-full border border-[#eadff0] bg-white px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-[#92278F]">
        {label ? <span className="text-[#8a7d74]">{label}:</span> : null}
        <span>{value}</span>
    </span>
);

const TraitCombinedCard = ({ trait, index }) => (
    <div className="full-results-dedicated-print-card rounded-[18px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
        <div className="flex items-start justify-between gap-2 border-b border-[#f1e8de] pb-2">
            <div>
                <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#8a7d74]">Top {index + 1} · {trait.code}</p>
                <h3 className="mt-0.5 text-[13px] leading-tight font-black text-[#171717] brand-font">{trait.heading}</h3>
            </div>
            <span className="rounded-full px-2 py-1 text-[8px] font-black uppercase tracking-[0.14em]" style={{ color: trait.color, backgroundColor: `${trait.color}12` }}>
                {trait.percent}%
            </span>
        </div>

        <div className="mt-2 space-y-1.35">
            {trait.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-[7.4px] leading-[1.25] text-[#57534e] brand-text">{paragraph}</p>
            ))}
        </div>

        <div className="mt-2 border-t border-[#f1e8de] pt-2">
            <p className="text-[8px] font-black uppercase tracking-[0.16em] text-[#92278F]">Possible careers</p>
            <p className="mt-1 text-[8px] leading-[1.25] text-[#57534e] brand-text">{trait.careerIntro}</p>
            <div className="mt-1.5 flex flex-wrap gap-1">
                {trait.careers.map((career) => (
                    <span key={`${trait.code}-${career}`} className="rounded-full border border-[#eee7df] bg-[#fffdfb] px-1.5 py-0.5 text-[6.8px] font-bold leading-[1.2] text-[#51443e]">
                        {career}
                    </span>
                ))}
            </div>
            <div className="mt-2">
                <GuidanceCard />
            </div>
        </div>
    </div>
);

const RapidDiagram = () => (
    <img
        src={RAPID_MODEL_IMAGE_SRC}
        alt="RAPiD model"
        className="mx-auto mt-2 h-auto w-[70mm] max-w-full object-contain"
    />
);

const ScaleBar = ({ label, values }) => (
    <div className="grid grid-cols-4 overflow-hidden rounded-[8px] text-center text-[7.5px] font-black text-white brand-text">
        <div className="bg-[#4f81bd] px-1 py-1.5">{label}</div>
        {values.map((value) => (
            <div key={value.label} className="px-1 py-1.5" style={{ backgroundColor: value.color }}>{value.label}</div>
        ))}
    </div>
);

const ChoiceMatrix = ({ matrix }) => {
    const columns = ['Calculated', 'Rational', 'Emotional'];

    return (
        <div className="overflow-hidden rounded-[10px] border border-[#efe6dd]">
            <div className="grid bg-white text-center text-[8px] font-black text-[#171717]" style={{ gridTemplateColumns: '22mm repeat(3, minmax(0, 1fr))' }}>
                <div className="px-1 py-1 text-left uppercase tracking-[0.1em] text-[#8a7d74]">Perspectives</div>
                {columns.map((column) => <div key={column} className="px-1 py-1">{column}</div>)}
            </div>
            {matrix.map((row, rowIndex) => (
                <div key={row.perspective} className="grid" style={{ gridTemplateColumns: '22mm repeat(3, minmax(0, 1fr))' }}>
                    <div className="border-t border-white bg-white px-1 py-1.5 text-[7px] font-black text-[#171717]">{row.perspective}</div>
                    {columns.map((column) => (
                        <div
                            key={`${row.perspective}-${column}`}
                            className={`${rowIndex === 0 ? 'bg-[#f79646] text-white' : rowIndex === 1 ? 'bg-[#f9d8c8] text-[#171717]' : 'bg-[#faebe6] text-[#171717]'} whitespace-pre-line border-l border-t border-white px-1 py-1.5 text-[6.4px] font-semibold leading-[1.2]`}
                        >
                            {row.cells[column]}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

const PillarPrintCard = ({ pillar }) => (
    <div className="full-results-dedicated-print-card rounded-[18px] border border-[#efe6dd] bg-white px-3 py-2.5 shadow-sm">
        <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="text-[14px] leading-tight font-black text-[#171717] brand-font">{pillar.title}</h3>
            <Chip value={pillar.chip} />
        </div>
        <div className="mt-2 space-y-1.4">
            {pillar.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-[8.8px] leading-[1.34] text-[#57534e] brand-text">{paragraph}</p>
            ))}
        </div>
    </div>
);

const FullResultsPrintPage = ({ report, participantName }) => {
    const safeName = participantName === 'Your' ? 'Student' : participantName;
    const participantLabel = `Prepared for ${safeName}`;
    const topTraits = report.partA.topTraits || [];
    const reference = report.partB.reference;
    const pillarCards = [
        { ...reference.pillars.resources, chip: report.partB.resolvedChoices.resources.label },
        { ...reference.pillars.attitude, chip: report.partB.resolvedChoices.attitude.label },
        { ...reference.pillars.perspectives, chip: report.partB.resolvedChoices.perspectives.label },
        { ...reference.pillars.decisionMaking, chip: report.partB.resolvedChoices.decision.label }
    ];

    return (
        <div className="full-results-dedicated-print-portal" aria-hidden="true">
            <div className="full-results-dedicated-print-surface mx-auto flex flex-col gap-3 bg-white text-[#171717]" style={surfaceStyle}>
                <PageFrame pageLabel="Page 1 of 5" title="Yancha Crystal Report" subtitle={participantLabel}>
                    <div className="flex flex-col gap-3">
                        <section className="full-results-dedicated-print-section rounded-[22px] border border-[#eee7df] bg-[radial-gradient(circle_at_top_left,_rgba(243,119,65,0.16),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(146,39,143,0.13),_transparent_36%),linear-gradient(180deg,_#ffffff_0%,_#fffdfb_100%)] px-5 py-4 shadow-sm">
                            <h2 className="mt-2 text-[29px] leading-[1.05] font-black text-[#171717] brand-font">Yancha Crystal Report for {safeName}</h2>
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                <Chip label="Interest code" value={report.interestCode} />
                                {partBChips(report).map((chip) => <Chip key={chip.label} label={chip.label} value={chip.value} />)}
                            </div>
                        </section>

                        <TextCard title={reference.recommended.title} paragraphs={reference.recommended.paragraphs} />

                        <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm">
                            <SectionTitle eyebrow={report.partA.intro.analysisTitle} title={report.partA.intro.title} />
                            <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0, 1fr) 64mm' }}>
                                <TextCard paragraphs={report.partA.intro.paragraphs} className="bg-[#fffdfb]" />
                                <TextCard title={report.partA.intro.careerPreferencesTitle} paragraphs={report.partA.intro.careerPreferences} className="bg-[linear-gradient(180deg,_#fff8f3_0%,_#ffffff_100%)]" />
                            </div>
                        </section>
                    </div>
                </PageFrame>

                <PageFrame pageLabel="Page 2 of 5" title="Part A Personality Inference" subtitle={`Top traits: ${report.interestCode}`}>
                    <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm">
                        <SectionTitle
                            eyebrow="Part A inference"
                            title={`Personality explanations and career options: ${report.interestCode}`}
                            intro="Each trait shows its explanation first and then the matching possible career options."
                        />
                        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                            {topTraits.map((trait, index) => <TraitCombinedCard key={trait.code} trait={trait} index={index} />)}
                        </div>
                    </section>
                </PageFrame>

                <PageFrame pageLabel="Page 3 of 5" title="Part B Career Choice" subtitle="R-A-P-D framework">
                    <div className="flex flex-col gap-3">
                        <TextCard title="Career choice context" paragraphs={reference.intro} />

                        <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm">
                            <div className="grid gap-3" style={{ gridTemplateColumns: '63mm minmax(0, 1fr)' }}>
                                <div>
                                    <h3 className="text-[15px] leading-tight font-black text-[#171717] brand-font">{reference.rapidModel.title}</h3>
                                    <p className="mt-1.5 text-[9.4px] leading-[1.38] text-[#57534e] brand-text">{reference.rapidModel.intro}</p>
                                    <RapidDiagram />
                                    <p className="mt-1.5 text-[9px] leading-[1.35] text-[#57534e] brand-text">{reference.rapidModel.note}</p>
                                </div>

                                <div>
                                    <h3 className="text-[15px] leading-tight font-black text-[#171717] brand-font">{reference.careerChoice.title}</h3>
                                    <p className="mt-1.5 text-[9.4px] leading-[1.35] text-[#57534e] brand-text">{reference.careerChoice.intro}</p>
                                    <div className="mt-2 space-y-2">
                                        <ScaleBar label="Resources" values={[{ label: 'Optimal Usage', color: '#00b050' }, { label: 'Under Usage', color: '#ffc000' }, { label: 'Over usage', color: '#ff0000' }]} />
                                        <ChoiceMatrix matrix={report.partB.choiceMatrix} />
                                        <ScaleBar label="Attitude" values={[{ label: 'Positive', color: '#00b050' }, { label: 'Neutral', color: '#ffc000' }, { label: 'Negative', color: '#ff0000' }]} />
                                    </div>
                                    <p className="mt-2 text-[9px] leading-[1.35] text-[#57534e] brand-text">{reference.careerChoice.note}</p>
                                </div>
                            </div>
                        </section>
                    </div>
                </PageFrame>

                <PageFrame pageLabel="Page 4 of 5" title="Part B R-A-P-D Pillars" subtitle="Resources · Attitude · Perspectives · Decision Making">
                    <section className="full-results-dedicated-print-section rounded-[20px] border border-[#efe6dd] bg-white px-3.5 py-3 shadow-sm">
                        <SectionTitle eyebrow="Part B detail" title="Your R-A-P-D interpretation" />
                        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                            {pillarCards.map((pillar) => <PillarPrintCard key={pillar.title} pillar={pillar} />)}
                        </div>
                    </section>
                </PageFrame>

                <PageFrame pageLabel="Page 5 of 5" title="Final Notes" subtitle={participantLabel}>
                    <div className="flex flex-col gap-3">
                        <TextCard title={reference.careerChoices.title} paragraphs={reference.careerChoices.paragraphs} note={reference.careerChoices.note} />
                        <RecommendationPrintCard paragraphs={report.partB.recommendation.paragraphs} />
                        <GuidanceCard />
                        <section className="full-results-dedicated-print-section rounded-[20px] border border-[#eadff0] bg-white px-4 py-3 text-center shadow-sm">
                            <p className="text-[12px] leading-[1.5] font-semibold italic text-[#57534e] brand-text">
                                These insights describe tendencies, not limits. Skills and behaviors can evolve with experience and support.
                            </p>
                        </section>
                        <p className="pt-1 text-center text-[10px] italic text-gray-400 brand-text">{report.footerNote}</p>
                    </div>
                </PageFrame>
            </div>
        </div>
    );
};

export default FullResultsPrintPage;
